/**
 * Communities Repository
 * Data access layer for communities feature
 * Wraps API calls with error handling and data transformation
 */

import { CommunitiesApi } from './communities-api';
import type {
  GetCommunitiesResponse,
  GetCommunityResponse,
  InsertCommunityRequest,
  InsertCommunityResponse,
  UpdateCommunityRequest,
  UpdateCommunityResponse,
  UpsertCommunityAccountRelationResponse,
} from '../types/communities-types';
import type { CommunityAccountsResponse } from '../types/community-accounts-types';

export class CommunitiesRepository {
  constructor(private readonly api: CommunitiesApi) {}

  /**
   * Get all communities with optional filters
   */
  async getCommunities(
    includeExpired: boolean,
    actorId?: number
  ): Promise<GetCommunitiesResponse> {
    try {
      return await this.api.getCommunities(includeExpired, actorId);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch communities';
      throw new Error(message);
    }
  }

  /**
   * Get a single community by ID
   */
  async getCommunityById(id: number): Promise<GetCommunityResponse> {
    try {
      return await this.api.getCommunityById(id);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to fetch community';
      throw new Error(message);
    }
  }

  /**
   * Create a new community
   */
  async createCommunity(data: InsertCommunityRequest): Promise<InsertCommunityResponse> {
    try {
      return await this.api.createCommunity(data);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to create community';
      throw new Error(message);
    }
  }

  /**
   * Update an existing community
   */
  async updateCommunity(
    id: number,
    data: UpdateCommunityRequest
  ): Promise<UpdateCommunityResponse> {
    try {
      return await this.api.updateCommunity(id, data);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update community';
      throw new Error(message);
    }
  }

  /**
   * Delete a community
   * @param id - Community profile ID
   * @param hard - If true, permanent deletion; if false, soft delete
   */
  async deleteCommunity(id: number, hard: boolean = false): Promise<void> {
    try {
      await this.api.deleteCommunity(id, hard);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to delete community';
      throw new Error(message);
    }
  }

  /**
   * Get all accounts associated with a community
   */
  async getCommunityAccounts(communityId: number): Promise<CommunityAccountsResponse> {
    try {
      return await this.api.getCommunityAccounts(communityId);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to fetch community accounts';
      throw new Error(message);
    }
  }

  /**
   * Upsert (insert or update) an account-community relationship with date range
   */
  async upsertAccountRelation(
    communityProfileId: number,
    accountId: number,
    startDate: string,
    expirationDate: string | null
  ): Promise<UpsertCommunityAccountRelationResponse> {
    try {
      return await this.api.upsertAccountRelation(communityProfileId, accountId, startDate, expirationDate);
    } catch (error: unknown) {
      console.error('[CommunitiesRepository] Failed to upsert account relation:', error);
      const message =
        error instanceof Error ? error.message : 'Failed to update account assignment';
      throw new Error(message);
    }
  }
}
